import { useState, useEffect, useRef } from 'react';
import { X, User, Mail, Shield, Phone, Save, Trash2, Upload, Camera } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { aurora } from '@/components/aurora';
import { cn, getErrorMessage } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import type { MyProfile } from '@easyflow/shared';

type UserProfile = MyProfile;

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProfileUpdate?: () => void;
}

export default function ProfileModal({ isOpen, onClose, onProfileUpdate }: ProfileModalProps) {
  const { updateUser } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<MyProfile>('/voip/my-profile');
      if (data.success && data.data) {
        const loaded = data.data;
        setProfile(loaded);
        setFirstName(loaded.first_name || '');
        setLastName(loaded.last_name || '');
        setAvatarPreview(null);
      }
    } catch (err) {
      setError('Errore nel caricamento del profilo');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchProfile();
    }
  }, [isOpen]);

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError(null);
      await apiClient.patch('/voip/my-profile', {
        first_name: firstName,
        last_name: lastName,
      });

      await fetchProfile();
      onProfileUpdate?.();
    } catch (err) {
      setError('Errore nel salvataggio del profilo');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Per favore seleziona un file immagine');
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('L\'immagine deve essere inferiore a 2MB');
      return;
    }

    try {
      setUploadingAvatar(true);
      setError(null);

      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target?.result as string;
        setAvatarPreview(base64);
        
        try {
          // Errori non-2xx arrivano come ApiError con il messaggio del backend.
          await apiClient.post('/voip/my-avatar', { avatar_url: base64 });

          updateUser({ avatarUrl: base64 });
          await fetchProfile();
          onProfileUpdate?.();
        } catch (uploadErr) {
          setAvatarPreview(null);
          setError(getErrorMessage(uploadErr) || 'Errore nel caricamento dell\'avatar');
          console.error(uploadErr);
        } finally {
          setUploadingAvatar(false);
        }
      };
      
      reader.onerror = () => {
        setError('Errore nella lettura del file');
        setUploadingAvatar(false);
      };
      
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Errore nel caricamento dell\'avatar');
      console.error(err);
      setUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      setSaving(true);
      setError(null);
      await apiClient.delete('/voip/my-avatar');

      updateUser({ avatarUrl: null });
      setAvatarPreview(null);
      await fetchProfile();
      onProfileUpdate?.();
    } catch (err) {
      setError('Errore nella rimozione dell\'avatar');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'super_admin': return 'Super Administrator';
      case 'admin': return 'Amministratore Tenant';
      case 'user': return 'Utente';
      case 'agent': return 'Agente';
      default: return role;
    }
  };

  const getInitials = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name.charAt(0)}${profile.last_name.charAt(0)}`.toUpperCase();
    }
    return 'U';
  };

  const currentAvatar = avatarPreview || profile?.avatar_url;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative z-50 w-full max-w-lg mx-4 bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-500 to-indigo-600">
          <h2
            className="text-xl font-semibold text-white tracking-tight"
            style={{ fontFamily: aurora.serif }}
          >
            Il Mio Profilo
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : error ? (
            <div className="text-center py-4 text-red-500 text-sm bg-red-50 dark:bg-red-900/20 rounded-lg mb-4">
              {error}
            </div>
          ) : null}
          
          {!loading && profile ? (
            <div className="space-y-6">
              {/* Avatar Section */}
              <div className="flex flex-col items-center space-y-4">
                <div className="relative group">
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  
                  {/* Avatar display */}
                  <div 
                    onClick={() => !uploadingAvatar && fileInputRef.current?.click()}
                    className="relative cursor-pointer"
                  >
                    {currentAvatar ? (
                      <img
                        src={currentAvatar}
                        alt="Avatar"
                        className="h-28 w-28 rounded-full object-cover ring-4 ring-blue-500/30 transition-all group-hover:ring-blue-500/50"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="h-28 w-28 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center ring-4 ring-blue-500/30 transition-all group-hover:ring-blue-500/50">
                        <span className="text-3xl font-bold text-white">{getInitials()}</span>
                      </div>
                    )}
                    
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      {uploadingAvatar ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
                      ) : (
                        <Camera className="h-6 w-6 text-white" />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Avatar action buttons */}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAvatar}
                    className="text-xs"
                  >
                    <Upload className="h-3 w-3 mr-1" />
                    {uploadingAvatar ? 'Caricamento...' : 'Carica Foto'}
                  </Button>
                  {(currentAvatar || profile.avatar_url) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleRemoveAvatar}
                      disabled={saving || uploadingAvatar}
                      className="text-xs text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Rimuovi
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Clicca sull'avatar o usa il pulsante per caricare una foto (max 2MB)</p>
              </div>

              {/* Profile Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" className="text-sm text-gray-600 dark:text-gray-400">
                    Nome
                  </Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-sm text-gray-600 dark:text-gray-400">
                    Cognome
                  </Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Read-only Info */}
              <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3 text-sm">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-400">{profile.email}</span>
                </div>
                
                <div className="flex items-center space-x-3 text-sm">
                  <Shield className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-400">{getRoleLabel(profile.role)}</span>
                </div>
                
                {profile.extension && (
                  <div className="flex items-center space-x-3 text-sm">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600 dark:text-gray-400">
                      Interno {profile.extension.number}
                      {profile.extension.display_name && ` - ${profile.extension.display_name}`}
                    </span>
                  </div>
                )}
                
                <div className="flex items-center space-x-3 text-sm">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600 dark:text-gray-400">
                    Tenant: {profile.tenant.name}
                  </span>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <Button variant="outline" onClick={onClose}>
            Chiudi
          </Button>
          <Button
            onClick={handleSaveProfile}
            disabled={saving || loading}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Salva Modifiche
          </Button>
        </div>
      </div>
    </div>
  );
}
